import type { Organization, Client, DirectStore, Workspaces } from '@/modules/shared/types'

export interface ApiWorkspace {
  id: string
  slug: string
  name: string
  type: 'agency' | 'personal'
}

export interface ApiWorkspaceClient {
  id: number
  slug: string
  name: string
  role: 'owner' | 'admin' | 'collaborator' | 'viewer'
  onboardingStatus: 'active' | 'onboarding'
  integrations: string[]
  isFavorite: boolean
  /** Onboarding workflow lifecycle phase (B4); absent on older API responses. */
  phase?: 'prospect' | 'contracted' | 'active'
  /** Present only for a staff self-serve demo/training store (D1); undefined otherwise. */
  demo?: {
    status: 'preparing' | 'ready' | 'failed'
    sourceStoreId: number
  }
}

export function mapApiOrg(w: ApiWorkspace): Organization {
  return { id: w.id, slug: w.slug, name: w.name }
}

export function mapApiClient(c: ApiWorkspaceClient, orgId: string): Client {
  return {
    id: String(c.id),
    organizationId: orgId,
    name: c.name,
    slug: c.slug,
    platform: 'tiktok',
    onboardingStatus: c.onboardingStatus,
    // Default 'active' so the main roster doesn't hide a client whose phase
    // is unknown (e.g. an API deployed before this field existed).
    phase: c.phase ?? 'active',
    role: c.role,
    isFavorite: c.isFavorite,
    demo: c.demo,
  }
}

export interface ApiDirectStore {
  id: number
  slug: string
  name: string
  role: string
  orgId: string
  orgSlug: string
  orgName: string
  onboardingStatus: 'active' | 'onboarding'
}

export interface ApiWorkspacesResponse {
  organizations: ApiWorkspace[]
  directStores: ApiDirectStore[]
}

const DIRECT_STORE_ROLES = ['owner', 'admin', 'collaborator', 'viewer'] as const
type DirectStoreRole = (typeof DIRECT_STORE_ROLES)[number]

function toDirectStoreRole(role: string): DirectStoreRole {
  return (DIRECT_STORE_ROLES as readonly string[]).includes(role)
    ? (role as DirectStoreRole)
    : 'viewer'
}

export function mapApiWorkspaces(api: ApiWorkspacesResponse): Workspaces {
  return {
    organizations: api.organizations.map(mapApiOrg),
    directStores: api.directStores.map((s): DirectStore => ({
      id: String(s.id),
      slug: s.slug,
      name: s.name,
      role: toDirectStoreRole(s.role),
      orgId: s.orgId,
      orgSlug: s.orgSlug,
      orgName: s.orgName,
      onboardingStatus: s.onboardingStatus,
    })),
  }
}
